"use client";

import { useEffect, useRef } from "react";

export default function PricingSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.1 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy"
            style={{ padding: "120px 0" }}
            aria-labelledby="pricing-heading"
            id="pricing-heading"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
                    {/* Left: Formula */}
                    <div className="flex flex-col gap-8">
                        <div className="reveal">
                            <span className="type-subheading">Pricing Methodology</span>
                        </div>
                        <h2
                            className="reveal reveal-delay-1"
                            style={{
                                fontFamily: "var(--font-serif)",
                                fontSize: "clamp(2rem, 3.5vw, 3rem)",
                                fontWeight: 400,
                                color: "var(--color-white)",
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Transparent, market-
                            <br />
                            referenced pricing.
                        </h2>
                        <p
                            className="reveal reveal-delay-2"
                            style={{
                                fontFamily: "var(--font-sans)",
                                fontSize: "1rem",
                                color: "rgba(255,255,255,0.55)",
                                lineHeight: "1.7",
                            }}
                        >
                            Our pricing is fully indexed to publicly available benchmarks.
                            There are no hidden fees, no opaque intermediary margins.
                            Every component of the price is disclosed in the term sheet.
                        </p>

                        {/* Formula box */}
                        <div
                            className="reveal reveal-delay-3"
                            style={{
                                background: "var(--color-navy-mid)",
                                border: "1px solid var(--color-navy)",
                                borderRadius: "4px",
                                padding: "36px",
                            }}
                        >
                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.6875rem",
                                    fontWeight: 600,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: "var(--color-white)",
                                    marginBottom: "20px",
                                }}
                            >
                                Standard Price Formula
                            </p>
                            <p
                                style={{
                                    fontFamily: "var(--font-serif)",
                                    fontSize: "clamp(1.25rem, 2vw, 1.75rem)",
                                    color: "var(--color-red)",
                                    lineHeight: 1.4,
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                P = LME<sub style={{ fontSize: "0.7em" }}>MA</sub> × (1 + δ) + F<sub style={{ fontSize: "0.7em" }}>log</sub>
                            </p>
                            <div className="flex flex-col gap-3 mt-6">
                                {[
                                    { sym: "P", def: "Invoice price per metric tonne (USD)" },
                                    { sym: "LME_MA", def: "LME Antimony Metal Monthly Average" },
                                    { sym: "δ", def: "Agreed premium/discount (basis points)" },
                                    { sym: "F_log", def: "Freight & insurance (CIF basis)" },
                                ]?.map((row) => (
                                    <div
                                        key={row?.sym}
                                        className="flex items-start gap-4"
                                        style={{
                                            paddingBottom: "10px",
                                            borderBottom: "1px solid rgba(198,161,74,0.12)",
                                        }}
                                    >
                                        <code
                                            style={{
                                                fontFamily: "JetBrains Mono, monospace",
                                                fontSize: "0.8125rem",
                                                color: "var(--color-red)",
                                                minWidth: "52px",
                                            }}
                                        >
                                            {row?.sym}
                                        </code>
                                        <p
                                            style={{
                                                fontFamily: "var(--font-sans)",
                                                fontSize: "0.875rem",
                                                color: "rgba(255,255,255,0.5)",
                                            }}
                                        >
                                            {row?.def}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <p
                            className="reveal reveal-delay-4"
                            style={{
                                fontFamily: "var(--font-sans)",
                                fontSize: "0.8125rem",
                                color: "rgba(255,255,255,0.3)",
                                lineHeight: "1.6",
                            }}
                        >
                            Fixed-price structures available for contracts ≤12 months.
                            Multi-year contracts may incorporate annual price reset
                            mechanisms indexed to CPI or LME rolling averages.
                        </p>
                    </div>

                    {/* Right: Pricing options */}
                    <div className="flex flex-col gap-6">
                        {[
                            {
                                type: "Floating Price",
                                badge: "Most Common",
                                badgeStyle: { background: "var(--color-silver-bg)", color: "var(--color-red)" },
                                features: [
                                    "Monthly LME-referenced price reset",
                                    "Full price transparency",
                                    "No basis risk for buyer",
                                    "Suitable for all contract durations",
                                    "Quarterly premium review option",
                                ],
                                cta: "Request Term Sheet",
                            },
                            {
                                type: "Fixed Price",
                                badge: "≤12 Months",
                                badgeStyle: { background: "var(--color-silver-bg)", color: "rgba(255,255,255,0.45)" },
                                features: [
                                    "Price locked at contract inception",
                                    "Budget certainty for buyer",
                                    "Premium applied to LME at signing",
                                    "Subject to credit approval",
                                    "Available in USD and EUR",
                                ],
                                cta: "Enquire",
                            },
                        ]?.map((opt, i) => (
                            <div
                                key={opt?.type}
                                className={`reveal reveal-delay-${i + 2}`}
                                style={{
                                    background: "rgba(255,255,255,0.04)",
                                    border: i === 0
                                        ? "1px solid rgba(255,255,255,0.3)"
                                        : "1px solid rgba(255,255,255,0.08)",
                                    borderRadius: "4px",
                                    padding: "32px",
                                }}
                            >
                                <div className="flex items-center justify-between mb-5">
                                    <p
                                        style={{
                                            fontFamily: "var(--font-serif)",
                                            fontSize: "1.375rem",
                                            fontWeight: 400,
                                            color: "#FFFFFF",
                                        }}
                                    >
                                        {opt?.type}
                                    </p>
                                    <span
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.6875rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                            padding: "4px 12px",
                                            borderRadius: "2px",
                                            ...opt?.badgeStyle,
                                        }}
                                    >
                                        {opt?.badge}
                                    </span>
                                </div>
                                <ul className="flex flex-col gap-3 mb-6">
                                    {opt?.features?.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3"
                                            style={{
                                                fontFamily: "var(--font-sans)",
                                                fontSize: "0.875rem",
                                                color: "rgba(255,255,255,0.55)",
                                                lineHeight: "1.5",
                                            }}
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="#b91c1c"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ marginTop: "3px", flexShrink: 0 }}
                                                aria-hidden="true"
                                            >
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}